import { useDraggable } from '@dnd-kit/core';
import dayjs from 'dayjs';
import { Clock, Loader2 } from 'lucide-react';
import { memo, type KeyboardEvent, type MouseEvent } from 'react';
import type { CrmDealListItem, CrmStage } from '@ip/shared-types';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { t } from '@/i18n';
import { initialsFromName } from '@/lib/hr';
import { formatMoney } from '@/utils/currency';
import {
  STALE_DAYS,
  daysInStage,
  daysSinceActivity,
  daysTone,
  dealHealth,
  fmtDate,
  healthTone,
  inStageLabel,
  isClosedDeal,
  overdueLabel,
  relativeDay,
} from './dealMath';
import { TONE_BORDER_L, TONE_TEXT } from './tones';

interface BodyProps {
  deal: CrmDealListItem;
  stage?: CrmStage;
  now: Date;
  pending?: boolean;
  className?: string;
}

/** Relative close chip: `za 12 dni` muted, `dziś` amber, `3 dni po terminie` destructive. */
function CloseChip({ deal, now }: { deal: CrmDealListItem; now: Date }) {
  if (!deal.expectedCloseAt) return null;
  const rel = relativeDay(deal.expectedCloseAt, now);
  if (rel.kind === 'today') {
    return <span className={TONE_TEXT.amber}>{t('crm.deals.relative.today')}</span>;
  }
  if (rel.kind === 'yesterday' || rel.kind === 'daysAgo') {
    return <span className={TONE_TEXT.destructive}>{overdueLabel(rel.days)}</span>;
  }
  return (
    <span className={TONE_TEXT.muted}>
      {rel.kind === 'tomorrow'
        ? t('crm.deals.relative.tomorrow')
        : t('crm.deals.relative.inDays', { days: rel.days })}
    </span>
  );
}

/**
 * Presentational card — also rendered inside `DragOverlay`, which is why it
 * carries no dnd-kit hooks of its own.
 */
export function DealCardBody({ deal, stage, now, pending, className }: BodyProps) {
  const closed = isClosedDeal(deal, stage);
  const health = dealHealth(deal, now, stage);
  const inStage = daysInStage(deal, now);
  const idle = daysSinceActivity(deal, now);

  return (
    <div
      className={cn(
        'bg-card min-h-[84px] rounded-md border border-l-2 p-2.5 text-left',
        // A closed card carries no health, hence no accent stripe.
        health === 'closed' ? 'border-l-transparent' : TONE_BORDER_L[healthTone(health)],
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="line-clamp-2 text-sm font-medium">{deal.name}</div>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="shrink-0">
              {deal.ownerDisplayName ? (
                <Avatar className="size-5">
                  <AvatarFallback className="text-[9px] font-medium">
                    {initialsFromName(deal.ownerDisplayName)}
                  </AvatarFallback>
                </Avatar>
              ) : (
                <span className="border-muted-foreground/50 block size-5 rounded-full border border-dashed" />
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent>{deal.ownerDisplayName ?? t('crm.deals.card.noOwner')}</TooltipContent>
        </Tooltip>
      </div>
      <div className="text-muted-foreground truncate text-xs">{deal.accountName}</div>
      <div className="mt-1 flex items-center justify-between gap-2 text-xs">
        <span className={cn('text-sm font-semibold tabular-nums', deal.valueNet === null && 'text-muted-foreground')}>
          {deal.valueNet === null ? '—' : formatMoney(deal.valueNet, deal.currency)}
        </span>
        {closed ? (
          <span className="text-muted-foreground">
            {deal.closedAt ? dayjs(deal.closedAt).format('DD.MM.YY') : ''}
          </span>
        ) : (
          <CloseChip deal={deal} now={now} />
        )}
      </div>
      {!closed ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="text-muted-foreground mt-1 flex items-center justify-between gap-2 text-[11px]">
              <span className={cn('flex items-center gap-1', TONE_TEXT[daysTone(inStage)])}>
                {pending ? <Loader2 className="size-3 animate-spin" /> : <Clock className="size-3" />}
                {inStageLabel(inStage)}
              </span>
              {idle >= STALE_DAYS ? (
                <span className={TONE_TEXT.destructive}>{t('crm.deals.card.noContact', { days: idle })}</span>
              ) : null}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {t('crm.deals.card.tooltip', {
              activity: deal.lastActivityAt ? fmtDate(deal.lastActivityAt) : t('crm.deals.card.noActivityYet'),
              since: fmtDate(deal.stageEnteredAt),
            })}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </div>
  );
}

interface CardProps {
  deal: CrmDealListItem;
  stage?: CrmStage;
  now: Date;
  pending: boolean;
  onOpen: (dealId: string) => void;
}

/**
 * Draggable wrapper. `role="button"` + tabIndex come from dnd-kit's
 * attributes; Enter opens the deal (Space is reserved for lifting the card).
 * Memoised: the board re-renders on every minute tick, every filter keystroke
 * and every dnd-kit context change — with stable `deal`/`stage`/`onOpen`
 * props only the cards whose data actually changed re-render.
 */
export const DealCard = memo(function DealCard({ deal, stage, now, pending, onOpen }: CardProps) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: deal.id,
    data: { stageId: deal.stageId },
    disabled: pending,
  });

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    onOpen(deal.id);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onOpen(deal.id);
      return;
    }
    // Space and the arrows belong to the keyboard sensor.
    listeners?.onKeyDown?.(event);
  };

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      onKeyDown={handleKeyDown}
      onClick={handleClick}
      data-deal-id={deal.id}
      aria-busy={pending || undefined}
      className={cn(
        'rounded-md outline-none transition-[opacity,box-shadow] motion-reduce:transition-none',
        'cursor-grab active:cursor-grabbing [touch-action:manipulation]',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-1',
        '[&>div]:hover:border-primary/40',
        pending && 'pointer-events-none opacity-60',
        isDragging && 'opacity-40',
      )}
    >
      <DealCardBody deal={deal} stage={stage} now={now} pending={pending} />
    </div>
  );
});
